import { useEffect, useRef, useState } from 'react'
import { loadGoogleMaps } from '#lib/google-maps'
import { cn } from '#lib/utils'

interface MapPoint {
	id: string
	/** Null for a residence with no position yet: it has no marker, but its address is listed if the map does not load. */
	latitude: number | null
	longitude: number | null
	name: string
	address: string
}

declare global {
	interface Window {
		/** Google calls it when the key is refused: invalid, over quota or not allowed for this address. */
		gm_authFailure?: () => void
	}
}

// Where the map starts and at what zoom, until the points are placed: Italy.
const ITALY = { lat: 42.5, lng: 12.5 }
const ITALY_ZOOM = 5
const SINGLE_POINT_ZOOM = 15
// The marker library needs a map id; this one is Google's, for maps with no
// cloud styling.
const MAP_ID = 'DEMO_MAP_ID'

/** The card a marker opens: built with the DOM, since the text comes from the data. */
function pointCard(
	point: { id: string; name: string; address: string },
	selectLabel: string | undefined,
	onSelect: (id: string) => void
) {
	const card = document.createElement('div')
	card.className = 'flex max-w-60 flex-col gap-1 font-sans text-sm'
	const name = document.createElement('strong')
	name.className = 'font-semibold'
	name.textContent = point.name
	const address = document.createElement('span')
	address.className = 'text-muted-foreground'
	address.textContent = point.address
	card.append(name, address)
	if (selectLabel) {
		const open = document.createElement('button')
		open.type = 'button'
		open.className =
			'mt-1 rounded-full bg-primary px-3 py-2 font-medium text-primary-foreground text-sm'
		open.textContent = selectLabel
		open.addEventListener('click', () => onSelect(point.id))
		card.append(open)
	}
	return card
}

/**
 * A Google map with a marker for each residence. It loads when it scrolls into
 * view, so a page that never shows it does not pay for it, and only in the
 * browser. With no key, or if Google cannot be loaded, it lists the addresses
 * as text instead. The texts come from the page: the component has no
 * language of its own.
 */
function ResidenceMap({
	points,
	apiKey,
	language,
	label,
	className,
	onSelect,
	selectLabel
}: {
	points: MapPoint[]
	/** The browser key of Google Maps Platform; empty means no map. */
	apiKey: string | undefined
	language: string
	/** Names the map for assistive technology. */
	label: string
	className?: string
	/**
	 * With it, a marker opens a small card with the name, the address and a button named
	 * `selectLabel`, which calls this with the id of the point: the map leads somewhere.
	 */
	onSelect?: (id: string) => void
	selectLabel?: string
}) {
	// Read when a marker is pressed, not when it is placed: a new function on every render must not
	// place the markers again.
	const select = useRef({ onSelect, selectLabel })
	select.current = { onSelect, selectLabel }
	const container = useRef<HTMLElement>(null)
	// What the loading step made, for the markers step: the map and the classes
	// the markers need from the libraries.
	const loaded = useRef<{
		map: google.maps.Map
		Marker: typeof google.maps.marker.AdvancedMarkerElement
		InfoWindow: typeof google.maps.InfoWindow
		LatLngBounds: typeof google.maps.LatLngBounds
	} | null>(null)
	const [status, setStatus] = useState<
		'idle' | 'visible' | 'ready' | 'failed'
	>('idle')

	useEffect(() => {
		if (!apiKey || status !== 'idle' || !container.current) {
			return
		}
		// A margin so the map is ready as it comes into view, and a map already
		// above the viewport (the page was scrolled before it was watched) loads too:
		// it would otherwise wait for the visitor to scroll back up.
		const observer = new IntersectionObserver(
			(entries) => {
				if (
					entries.some(
						(entry) =>
							entry.isIntersecting ||
							entry.boundingClientRect.bottom < 0
					)
				) {
					setStatus('visible')
					observer.disconnect()
				}
			},
			{ rootMargin: '300px' }
		)
		observer.observe(container.current)
		return () => observer.disconnect()
	}, [apiKey, status])

	// Google loads the script fine with a key it then refuses, and shows its
	// own error over the map: the only signal is this global callback.
	useEffect(() => {
		if (status !== 'visible' && status !== 'ready') {
			return
		}
		const before = window.gm_authFailure
		window.gm_authFailure = () => setStatus('failed')
		return () => {
			window.gm_authFailure = before
		}
	}, [status])

	useEffect(() => {
		if (!apiKey || status !== 'visible') {
			return
		}
		let cancelled = false
		Promise.all([
			loadGoogleMaps(apiKey, 'maps', language),
			loadGoogleMaps(apiKey, 'marker', language),
			loadGoogleMaps(apiKey, 'core', language)
		])
			.then(([maps, markers, core]) => {
				if (cancelled || !container.current) {
					return
				}
				loaded.current = {
					map: new maps.Map(container.current, {
						mapId: MAP_ID,
						center: ITALY,
						zoom: ITALY_ZOOM,
						mapTypeControl: false,
						streetViewControl: false,
						fullscreenControl: false
					}),
					Marker: markers.AdvancedMarkerElement,
					InfoWindow: maps.InfoWindow,
					LatLngBounds: core.LatLngBounds
				}
				setStatus('ready')
			})
			.catch(() => {
				if (!cancelled) {
					setStatus('failed')
				}
			})
		return () => {
			cancelled = true
		}
	}, [apiKey, language, status])

	// The parent builds `points` anew on every render: the markers follow what
	// the points say, not the array, or every render of the page would place
	// them again and put the map back where it started.
	const placedKey = JSON.stringify(
		points.flatMap((point) =>
			point.latitude !== null && point.longitude !== null
				? [
						[
							point.id,
							point.name,
							point.address,
							point.latitude,
							point.longitude
						]
					]
				: []
		)
	)

	useEffect(() => {
		const current = loaded.current
		if (status !== 'ready' || !current) {
			return
		}
		const { map, Marker, InfoWindow, LatLngBounds } = current
		const placed = (
			JSON.parse(placedKey) as [string, string, string, number, number][]
		).map(([id, name, address, lat, lng]) => ({
			id,
			name,
			address,
			position: { lat, lng }
		}))
		const card = new InfoWindow()
		const markers = placed.map((point) => {
			const marker = new Marker({
				map,
				position: point.position,
				title: point.name,
				gmpClickable: Boolean(select.current.onSelect)
			})
			if (select.current.onSelect) {
				marker.addEventListener('gmp-click', () => {
					card.setContent(
						pointCard(point, select.current.selectLabel, (id) =>
							select.current.onSelect?.(id)
						)
					)
					card.open({ map, anchor: marker })
				})
			}
			return marker
		})
		const [only] = placed
		if (placed.length === 1 && only) {
			map.setCenter(only.position)
			map.setZoom(SINGLE_POINT_ZOOM)
		} else if (placed.length > 1) {
			const bounds = new LatLngBounds()
			for (const point of placed) {
				bounds.extend(point.position)
			}
			map.fitBounds(bounds, 48)
		} else {
			map.setCenter(ITALY)
			map.setZoom(ITALY_ZOOM)
		}
		return () => {
			card.close()
			for (const marker of markers) {
				// Google throws here when the map never finished starting (the page was
				// left or the key was refused): the marker goes with the map, and an error
				// in a cleanup would take the whole page down with it.
				try {
					marker.map = null
				} catch {
					// Nothing to take off a map that never started.
				}
			}
		}
	}, [placedKey, status])

	if (!apiKey || status === 'failed') {
		return (
			<ul
				aria-label={label}
				className={cn('flex flex-col gap-2 text-sm', className)}
			>
				{points.map((point) => (
					<li key={point.id}>
						<span className='block font-medium'>{point.name}</span>
						<span className='block text-muted-foreground'>
							{point.address}
						</span>
					</li>
				))}
			</ul>
		)
	}

	return (
		<section
			aria-label={label}
			className={cn(
				'h-72 w-full overflow-hidden rounded-xl border border-border bg-muted',
				className
			)}
			ref={container}
		/>
	)
}

export { ResidenceMap }
export type { MapPoint }
